import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  paymentGatewayFormInput,
  type PaymentGatewayDetail,
  type PaymentGatewayFormInput,
} from '../api/schemas.js';
import { useCreatePaymentGateway, useUpdatePaymentGateway } from '../api/mutations.js';
import { usePaymentGateway } from '../api/queries.js';
import { PaymentGatewayFields } from '../forms/payment-gateway-fields.js';

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

export const CREATE_DEFAULTS: PaymentGatewayFormInput = {
  provider: '',
  name: '',
  isActive: true,
  isDefault: false,
  windcaveBaseUrl: '',
  windcaveUsername: '',
  windcaveAuthKey: '',
  stripePublishableKey: '',
  stripeSecretKey: '',
  westpacMerchantId: '',
  westpacApiKey: '',
  westpacPassword: '',
};

function defaultsFrom(gateway: PaymentGatewayDetail): PaymentGatewayFormInput {
  return {
    ...CREATE_DEFAULTS,
    provider: gateway.provider,
    name: gateway.name,
    isActive: gateway.isActive,
    isDefault: gateway.isDefault,
  };
}

export function PaymentGatewayFormPage(): React.JSX.Element {
  const gatewayId = useParams().gatewayId ?? '';
  const isEdit = gatewayId !== '';

  return (
    <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE} fallback={<NotPermitted />}>
      {isEdit ? <EditPaymentGateway gatewayId={gatewayId} /> : <CreatePaymentGateway />}
    </PermissionGuard>
  );
}

function CreatePaymentGateway(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreatePaymentGateway();

  const form = useAppForm<PaymentGatewayFormInput>({
    schema: paymentGatewayFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Payment gateway created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/payment-gateways/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New payment gateway"
      description="A payment gateway is the provider connection this tenant takes payment through."
      backTo="/payment-gateways"
      backLabel="Payment gateways"
      submitLabel="Create gateway"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PaymentGatewayFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
      />
    </FormPage>
  );
}

function EditPaymentGateway({ gatewayId }: { readonly gatewayId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdatePaymentGateway();
  const query = usePaymentGateway(gatewayId);

  if (query.isPending) return <LoadingState label="Loading gateway" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditPaymentGatewayForm
      gateway={query.data}
      onSaved={() => {
        void navigate(`/payment-gateways/${gatewayId}`);
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ gatewayId, version: query.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditPaymentGatewayForm({
  gateway,
  onSaved,
  save,
}: {
  readonly gateway: PaymentGatewayDetail;
  readonly onSaved: () => void;
  readonly save: (values: PaymentGatewayFormInput) => Promise<void>;
}): React.JSX.Element {
  const form = useAppForm<PaymentGatewayFormInput>({
    schema: paymentGatewayFormInput,
    defaultValues: defaultsFrom(gateway),
    successMessage: 'Payment gateway updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title="Edit payment gateway"
      description="Change the gateway's settings. Credentials are not shown for security — enter a complete set only when you want to replace them."
      backTo="/payment-gateways"
      backLabel="Payment gateways"
      submitLabel="Save gateway"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PaymentGatewayFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
      />
    </FormPage>
  );
}
